import { Box3, Vector3 } from 'three';
import { cloneGltf, getEnemyAsset, getHeroAsset } from '../engine/loaders.js';

const box = new Box3();
const size = new Vector3();
const center = new Vector3();

export function applyEnemyModel(entity, type, fallbackHeight = 1.8) {
  const loaded = getEnemyAsset(type);
  const asset = loaded?.asset;
  if (!entity?.group || !asset) {
    setProceduralVisible(entity, true);
    removeAssetModel(entity);
    return false;
  }

  removeAssetModel(entity);
  const model = cloneGltf(asset.scene || asset);
  normalizeModel(model, loaded.entry, fallbackHeight, entity.group.scale.x || 1);
  model.name = `moggmon-${type}`;
  model.userData.assetModel = true;
  model.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
      boostChibiEmissive(child);
    }
  });
  entity.group.add(model);
  entity.assetModel = model;
  setProceduralVisible(entity, false);
  return true;
}

export function removeAssetModel(entity) {
  if (!entity?.assetModel) return;
  entity.group.remove(entity.assetModel);
  entity.assetModel = null;
}

function normalizeModel(model, entry = {}, fallbackHeight, parentScale = 1) {
  box.setFromObject(model);
  box.getSize(size);
  const targetHeight = (entry.height || fallbackHeight) / Math.max(parentScale, 0.001);
  model.scale.setScalar(targetHeight / Math.max(size.y, 0.001));
  model.rotation.y = Number.isFinite(entry.rotateY) ? entry.rotateY : 0;
  box.setFromObject(model);
  box.getCenter(center);
  model.position.x -= center.x;
  model.position.z -= center.z;
  model.position.y -= box.min.y;
  model.userData.basePosition = model.position.clone();
  model.userData.baseRotation = model.rotation.clone();
  model.userData.baseScale = model.scale.clone();
}

function setProceduralVisible(entity, visible) {
  entity?.group?.traverse?.((child) => {
    if (child.userData?.proceduralEnemy) child.visible = visible;
  });
}

// ─── Hero (player) model support ────────────────────────────────────────────

export function applyHeroModel(playerEntity, heroId = null) {
  const loaded = getHeroAsset(heroId);
  const asset = loaded?.asset;
  if (!playerEntity?.group || !asset) return false;

  removeHeroModel(playerEntity);
  const model = cloneGltf(asset.scene || asset);
  normalizeModel(model, loaded.entry, 2.0, 1);
  model.name = 'moggmon-hero';
  model.userData.heroModel = true;
  model.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
      boostChibiEmissive(child);
    }
  });
  playerEntity.group.add(model);
  playerEntity.heroModel = model;
  setHeroProceduralVisible(playerEntity, false);
  return true;
}

export function removeHeroModel(playerEntity) {
  if (!playerEntity?.heroModel) return;
  playerEntity.group.remove(playerEntity.heroModel);
  playerEntity.heroModel = null;
  setHeroProceduralVisible(playerEntity, true);
}

function setHeroProceduralVisible(playerEntity, visible) {
  playerEntity?.group?.traverse?.((child) => {
    if (child.userData?.proceduralPlayer) child.visible = visible;
  });
}

/** Give chibi mesh materials a slight self-illumination so they read clearly
 *  in the dark neon arena even before full lighting is on them. */
function boostChibiEmissive(mesh) {
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const mat of mats) {
    if (!mat || !mat.isMeshStandardMaterial) continue;
    // Only boost if emissive is still the default black
    if (mat.emissive && (mat.emissive.r > 0.01 || mat.emissive.g > 0.01 || mat.emissive.b > 0.01)) continue;
    mat.emissive = mat.color.clone().multiplyScalar(0.22);
    mat.emissiveIntensity = 1;
  }
}
